import React, { useEffect } from "react";

import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ConfigProvider, Layout, theme } from "antd";

import { Account } from "../layouts/account";
import { Billings } from "../layouts/billings";
import { Cloud } from "../layouts/cloud";
import { InstanceGroup } from "../layouts/instanceGroup";
import { Dashboard } from "../layouts/dashboard";
import { Instances } from "../layouts/instances";
import { Templates } from "../layouts/templates";
import { Payment } from "../layouts/payment";
import { NotFound } from "../layouts/notFound";
import DownloadProgress from "../layouts/downloadProgress";

import { useAppSelector } from "../store/hooks";

import { MainMenu } from "../components/mainMenu";
import DockerFileList from "../components/DockerFileList";
import { BalanceCredit } from "../components/BalanceCredit";
import { ProviderMainMenu } from "../components/providerMainMenu";
import InstanceStatus from "../components/InstanceStatus";
import { InstanceDetail } from "../layouts/instanceDetail";
import { RentSummary } from "../layouts/rentSummary";
import { Installation } from "../layouts/installation";
import { MachineStatus } from "../layouts/machineStatus";
import { Profits } from "../layouts/profits";
import { ESGReport } from "../layouts/esgReport";
import ProviderMachineStatus from "../components/ProviderMachineStatus";

import { AuthProvider } from "../utility/AuthContext";
import GlobalInterceptor from "../utility/GlobalInterceptor";
import LoginModal from "../components/LoginModal";
import { LoggedInAvatar } from "../components/LoggedInAvatar";
import { Usage } from "../layouts/usage";
import { Docs } from "../layouts/docs";
import Privacy from "./Privacy";
import TermsOfService from "./TermsOfService";
import OAuthCallback from "./OAuthCallback";

const { Header, Content } = Layout;

const Main: React.FC = () => {
  const { t } = useTranslation();
  const userState = useAppSelector((state: any) => state.user);
  const cloudState = useAppSelector((state: any) => state.cloud);
  let location = useLocation();

  return (
    <AuthProvider>
      <GlobalInterceptor />
      <LoginModal />
      <ConfigProvider
        theme={{
          hashed: false,
          token: {
            colorPrimary: "#5575D9",
          },
          components: {
            Layout: {
              siderBg: "#1a1a1a",
              triggerBg: "#1F263E",
            },
            Menu: {
              darkItemBg: "transparent",
              darkItemSelectedBg: "transparent",
              groupTitleColor: "#bdbdbd",
              subMenuItemBg: "transparent",
            },
          },
        }}
      >
        <Layout className="main-wrapper">
          <Header className="header-wrapper">
            <div className="logo-wrapper">
              <Link data-no-auth to="/">
                <div className="logo-black" />
              </Link>
              {/* <img
                style={{ cursor: "pointer" }}
                src="/images/icons/hide-menu.svg"
                height={"32px"}
              /> */}
            </div>
            <div className="hidden-description">
              <p style={{ color: "#fbfbfb", fontSize: "1px", cursor: "none" }}>
                {t(
                  "NeuroPlus is a cloud GPU computing platform for renting and managing GPU instances for AI and machine learning workloads.",
                )}
              </p>
            </div>

            <div className="top-right-menu-items">
              <BalanceCredit />
              <LoggedInAvatar />
            </div>
          </Header>
          <div className="main-content">
            {userState.is_provider ? <ProviderMainMenu /> : <MainMenu />}
            <Layout
              className={
                location.pathname == "/esgReport"
                  ? "layout esg-report-custom"
                  : "layout"
              }
            >
              {userState.is_provider ? (
                <ProviderMachineStatus />
              ) : (
                <InstanceStatus />
              )}

              <Content className="content">
                {userState.is_provider ? (
                  <Routes>
                    <Route path={`/installation`} element={<Installation />} />
                    <Route path={`/profits`} element={<Profits />} />
                    <Route
                      path={`/machineStatus`}
                      element={<MachineStatus />}
                    />
                  </Routes>
                ) : (
                  <Routes>
                    <Route path={`/`} element={<Dashboard />} />
                    <Route path={`/account`} element={<Account />} />
                    <Route path={`/billings`} element={<Billings />} />
                    <Route path={`/cloud`} element={<Cloud />} />
                    <Route
                      path={`/instanceGroup`}
                      element={<InstanceGroup />}
                    />
                    <Route
                      path={"/downloadProgress"}
                      element={<DownloadProgress />}
                    />
                    <Route path={"instances"} element={<Instances />} />
                    <Route path={"esgReport"} element={<ESGReport />} />

                    <Route
                      path={"/dockerlist"}
                      element={
                        <DockerFileList dockerList={cloudState.dockerList} />
                      }
                    />
                    <Route path={"/instances/*"} element={<InstanceDetail />} />
                    <Route path={`/rentSummary`} element={<RentSummary />} />
                    <Route path={`/usage`} element={<Usage />} />
                    <Route path={`/templates`} element={<Templates />} />
                    <Route path={`/docs`} element={<Docs />} />
                    <Route path={`/payment`} element={<Payment />} />
                    <Route path={"/privacy"} element={<Privacy />} />
                    <Route path={"/terms"} element={<TermsOfService />} />
                    <Route path="/oauth/callback" element={<OAuthCallback />} />
                    <Route path={"/404"} element={<NotFound />} />
                    <Route path="*" element={<Navigate to="/404" />} />
                  </Routes>
                )}
              </Content>
            </Layout>
          </div>
        </Layout>
      </ConfigProvider>
    </AuthProvider>
  );
};

export default Main;
